import { z } from 'zod';
import { GetAgreementResponse } from '@/api/generated/endpoints/agreements/agreements.zod';
import {
  GetContractByIdResponse,
  GetContractsResponse,
} from '@/api/generated/endpoints/contracts/contracts.zod';
import { GetVinDetailResponse } from '@/api/generated/endpoints/vehicle/vehicle.zod';
import {
  ContractCancellationDate,
  ContractExpiryDate,
  ContractRegistrationDate,
  ContractStartDate,
  CustomerBuildDate,
  CustomerLastChangedDate,
  DeliveryDate,
  LastRecordedAt,
  PaymentDueDate,
  PaymentInvoiceDate,
  PaymentReceivedDate,
  WarrantyStartDate,
} from '@/lib/dates';

export const ContractSummary =
  GetContractsResponse.shape.contracts.element.extend({
    startDate: ContractStartDate,
    expiryDate: ContractExpiryDate,
    cancellationDate: ContractCancellationDate.nullable(),
  });
export type ContractSummary = z.infer<typeof ContractSummary>;

export const ContractPage = GetContractsResponse.extend({
  contracts: z.array(ContractSummary),
});
export type ContractPage = z.infer<typeof ContractPage>;

const ContractVehicle = GetContractByIdResponse.shape.vehicle.unwrap().extend({
  deliveryDate: DeliveryDate.nullish(),
  lastRecordedAt: LastRecordedAt.nullish(),
});

export const ContractDetails = GetContractByIdResponse.extend({
  registrationDate: ContractRegistrationDate,
  startDate: ContractStartDate,
  expiryDate: ContractExpiryDate,
  cancellationDate: ContractCancellationDate.nullish(),
  vehicle: ContractVehicle.optional(),
});
export type ContractDetails = z.infer<typeof ContractDetails>;

export const VinDetail = GetVinDetailResponse.extend({
  deliveryDate: DeliveryDate.nullish(),
  lastRecordedAt: LastRecordedAt.nullish(),
  warrantyStartDate: WarrantyStartDate.nullish(),
  customerBuildDate: CustomerBuildDate.nullish(),
  customerLastChangedDate: CustomerLastChangedDate.nullish(),
});
export type VinDetail = z.infer<typeof VinDetail>;

const GeneratedSchedule = GetAgreementResponse.shape.schedule.unwrap();

const ScheduledPayment = GeneratedSchedule.shape.payments.element.extend({
  invoiceDate: PaymentInvoiceDate,
  dueDate: PaymentDueDate,
  receivedDate: PaymentReceivedDate.nullable(),
});
export type ScheduledPayment = z.infer<typeof ScheduledPayment>;

export const PaymentSchedule = GeneratedSchedule.extend({
  payments: z.array(ScheduledPayment),
});
export type PaymentSchedule = z.infer<typeof PaymentSchedule>;

export const AgreementDetails = GetAgreementResponse.extend({
  contracts: z.array(ContractSummary),
  schedule: PaymentSchedule.nullable(),
});
export type AgreementDetails = z.infer<typeof AgreementDetails>;
